/**
 * useAgentActions — OS Agent Action Dispatcher
 *
 * RunAI OS'un sinir sistemi. Agent her şeyi kontrol eder:
 * - Window aç/kapat/taşı/sabitle
 * - Wallpaper, tema, ses, mikrofon
 * - Dock, chat pozisyonu
 * - Widget güncelle/ekle
 * - Skill çalıştır
 * - Bildirim gönder
 * - Terminal komutu
 * - Spotlight aç
 *
 * Yeni servis eklenince buraya action handler ekle (Kural 17).
 *
 * Format: ---ACTION: ACTION_NAME | param1 | param2
 */

// ── Action Types ──────────────────────────────────────────────────────────────

export type OSActionType =
  // Window Management
  | 'OPEN_WINDOW' | 'CLOSE_WINDOW' | 'MINIMIZE_WINDOW' | 'MAXIMIZE_WINDOW'
  | 'PIN_WINDOW' | 'UNPIN_WINDOW' | 'FOCUS_WINDOW'
  // OS Appearance
  | 'CHANGE_WALLPAPER' | 'CHANGE_THEME'
  | 'MOVE_DOCK' | 'MOVE_CHAT'
  // Media
  | 'TOGGLE_MIC' | 'TOGGLE_TTS' | 'CHANGE_VOLUME' | 'FULLSCREEN'
  // Widgets
  | 'UPDATE_WIDGET' | 'SHOW_WIDGET' | 'HIDE_WIDGET'
  // Notifications
  | 'SHOW_NOTIFICATION' | 'SPOTLIGHT_SEARCH'
  // Backend (handled in chat.rs, frontend shows result)
  | 'CREATE_TASK' | 'RESEARCH' | 'RUN_SKILL' | 'SOCIAL_POST'
  | 'BRAIN_LEARN' | 'CALENDAR_ADD' | 'OPEN_TERMINAL' | 'CREATE_NOTE'
  // New OS actions
  | 'CHECK_BALANCE' | 'LIST_AGENTS' | 'INSTALL_SKILL'
  | 'LIST_FILES' | 'SAVE_FILE' | 'READ_FILE' | 'DELETE_FILE' | 'SET_AGENT_MODE'
  // Canvas (RunCanvas)
  | 'CANVAS_NEW' | 'CANVAS_ADD_TEXT' | 'CANVAS_ADD_IMAGE'
  | 'CANVAS_EXPORT_PDF' | 'CANVAS_EXPORT_PPTX'
  // Office (RunOffice)
  | 'OFFICE_NEW' | 'OFFICE_INSERT_TEXT'
  // Music (RunMusic)
  | 'PLAY_MUSIC'
  // Workspace
  | 'SWITCH_WORKSPACE'
  // Routines
  | 'RUN_ROUTINE'
  // Browser (RunWeb)
  | 'BROWSE_URL' | 'WEB_SUMMARIZE' | 'ADD_BOOKMARK'
  // Note / Pad
  | 'WRITE_NOTE'
  // FlzHub
  | 'FLZHUB_POST'
  // Twin
  | 'TWIN_LEARN'
  // Admin
  | 'ADMIN_ACTION'
  // Training
  | 'START_TRAINING'
  // RunCode
  | 'CODE_RUN' | 'CODE_WRITE' | 'BUILDER_OPEN'
  // Unknown
  | 'UNKNOWN'

export interface AgentAction {
  type: OSActionType
  params: string[]
  raw: string
}

// ── Parser ────────────────────────────────────────────────────────────────────

/**
 * Parse ---ACTION: TYPE | param1 | param2 tags from LLM response.
 * Supports multiple actions per response.
 */
export function parseAgentActions(text: string): AgentAction[] {
  const actions: AgentAction[] = []
  const lines = text.split('\n')

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed.startsWith('---ACTION:')) continue

    const withoutPrefix = trimmed.replace('---ACTION:', '').trim()
    const parts = withoutPrefix.split('|').map(p => p.trim())
    const actionType = parts[0].toUpperCase().replace(/-/g, '_') as OSActionType
    const params = parts.slice(1)

    actions.push({ type: actionType, params, raw: trimmed })
  }

  return actions
}

/**
 * Strip action tags from response text for display.
 */
export function stripActionTags(text: string): string {
  return text
    .split('\n')
    .filter(line => !line.trim().startsWith('---ACTION:'))
    .join('\n')
    .trim()
}

// ── Dispatcher ────────────────────────────────────────────────────────────────

/**
 * Dispatch all parsed actions to the OS.
 * Returns list of executed action descriptions for chat display.
 */
export function dispatchAgentActions(actions: AgentAction[]): string[] {
  const executed: string[] = []

  for (const action of actions) {
    const result = dispatchSingleAction(action)
    if (result) executed.push(result)
  }

  return executed
}

function dispatchSingleAction(action: AgentAction): string | null {
  const [p1, p2] = action.params

  switch (action.type) {

    // ── Window Management ──────────────────────────────────────────────────
    case 'OPEN_WINDOW':
      if (p1) {
        window.dispatchEvent(new CustomEvent('runai:open-window', { detail: p1 }))
        return `🪟 Opened: ${p1}`
      }
      return null

    case 'CLOSE_WINDOW':
      if (p1) {
        window.dispatchEvent(new CustomEvent('runai:close-window', { detail: p1 }))
        return `✖ Closed: ${p1}`
      }
      return null

    case 'MINIMIZE_WINDOW':
      if (p1) {
        window.dispatchEvent(new CustomEvent('runai:minimize-window', { detail: p1 }))
        return `⬇ Minimized: ${p1}`
      }
      return null

    case 'MAXIMIZE_WINDOW':
      if (p1) {
        window.dispatchEvent(new CustomEvent('runai:maximize-window', { detail: p1 }))
        return `⬆ Maximized: ${p1}`
      }
      return null

    case 'PIN_WINDOW':
      if (p1) {
        window.dispatchEvent(new CustomEvent('runai:pin-window', { detail: { id: p1, corner: p2 || 'top-right' } }))
        return `📌 Pinned: ${p1}`
      }
      return null

    case 'FOCUS_WINDOW':
      if (p1) {
        window.dispatchEvent(new CustomEvent('runai:focus-window', { detail: p1 }))
        return `👁 Focused: ${p1}`
      }
      return null

    // ── OS Appearance ──────────────────────────────────────────────────────
    case 'CHANGE_WALLPAPER': {
      const preset = p1 || 'midnight'
      localStorage.setItem('os-wallpaper', preset)
      window.dispatchEvent(new CustomEvent('runai:wallpaper-change', { detail: preset }))
      return `🖼 Wallpaper: ${preset}`
    }

    case 'CHANGE_THEME': {
      const theme = (p1 || 'dark').toLowerCase()
      window.dispatchEvent(new CustomEvent('runai:theme-change', { detail: theme }))
      return `🎨 Theme: ${theme}`
    }

    case 'MOVE_DOCK': {
      const pos = (p1 || 'bottom').toLowerCase()
      window.dispatchEvent(new CustomEvent('runai:dock-position', { detail: pos }))
      return `🔲 Dock: ${pos}`
    }

    case 'MOVE_CHAT': {
      const pos = (p1 || 'right').toLowerCase()
      window.dispatchEvent(new CustomEvent('runai:sidechat-position', { detail: pos }))
      return `💬 Chat: ${pos}`
    }

    // ── Media ──────────────────────────────────────────────────────────────
    case 'TOGGLE_MIC': {
      const state = (p1 || 'toggle').toLowerCase()
      window.dispatchEvent(new CustomEvent('runai:toggle-mic', { detail: state }))
      return `🎤 Mic: ${state}`
    }

    case 'TOGGLE_TTS': {
      const state = (p1 || 'toggle').toLowerCase()
      window.dispatchEvent(new CustomEvent('runai:toggle-tts', { detail: state }))
      return `🔊 TTS: ${state}`
    }

    case 'CHANGE_VOLUME': {
      const vol = parseFloat(p1 || '0.5')
      // Set volume on all audio elements
      document.querySelectorAll('audio, video').forEach(el => {
        (el as HTMLMediaElement).volume = Math.max(0, Math.min(1, vol))
      })
      window.dispatchEvent(new CustomEvent('runai:volume-change', { detail: vol }))
      return `🔉 Volume: ${Math.round(vol * 100)}%`
    }

    case 'FULLSCREEN': {
      const on = (p1 || 'on').toLowerCase() === 'on'
      if (on && !document.fullscreenElement) {
        document.documentElement.requestFullscreen?.().catch(() => {})
      } else if (!on && document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => {})
      }
      return `⛶ Fullscreen: ${on ? 'on' : 'off'}`
    }

    // ── Widgets ────────────────────────────────────────────────────────────
    case 'UPDATE_WIDGET':
      window.dispatchEvent(new CustomEvent('runai:widget-update', { detail: { id: p1 } }))
      window.dispatchEvent(new CustomEvent('runai:task-alert', { detail: { type: 'refresh' } }))
      return `📊 Widget updated: ${p1 || 'all'}`

    case 'SHOW_WIDGET':
      window.dispatchEvent(new CustomEvent('runai:widget-visibility', { detail: { id: p1, visible: true } }))
      // Also store in localStorage for persistence
      try {
        const hidden = JSON.parse(localStorage.getItem('runai_hidden_widgets') || '[]') as string[]
        localStorage.setItem('runai_hidden_widgets', JSON.stringify(hidden.filter((w: string) => w !== p1)))
      } catch { /* */ }
      return `👁 Widget shown: ${p1}`

    case 'HIDE_WIDGET':
      window.dispatchEvent(new CustomEvent('runai:widget-visibility', { detail: { id: p1, visible: false } }))
      try {
        const hidden = JSON.parse(localStorage.getItem('runai_hidden_widgets') || '[]') as string[]
        if (!hidden.includes(p1)) hidden.push(p1)
        localStorage.setItem('runai_hidden_widgets', JSON.stringify(hidden))
      } catch { /* */ }
      return `🙈 Widget hidden: ${p1}`

    // ── Notifications ──────────────────────────────────────────────────────
    case 'SHOW_NOTIFICATION':
      window.dispatchEvent(new CustomEvent('runai:toast', { detail: { message: p1 || 'Agent notification', type: p2 || 'info' } }))
      return `🔔 Notification sent`

    case 'SPOTLIGHT_SEARCH':
      window.dispatchEvent(new CustomEvent('runai:spotlight-open', { detail: p1 || '' }))
      return `🔍 Spotlight: ${p1 || 'opened'}`

    // ── Backend Actions (chat.rs handles, frontend shows result) ──────────
    case 'CREATE_TASK':
      window.dispatchEvent(new CustomEvent('runai:task-alert', { detail: { type: 'created', title: p1 } }))
      return `✅ Task: "${p1}"`

    case 'RUN_SKILL':
      // Open skills window and dispatch skill run event
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'skills-window' }))
      window.dispatchEvent(new CustomEvent('runai:skill-run', { detail: { name: p1, query: p2 } }))
      return `⚡ Skill: ${p1}`

    case 'OPEN_TERMINAL':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-terminal' }))
      if (p1) window.dispatchEvent(new CustomEvent('runai:terminal-command', { detail: p1 }))
      return `💻 Terminal: ${p1 || 'opened'}`

    case 'SOCIAL_POST':
      // Open social window — backend already created the post
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'social-window' }))
      window.dispatchEvent(new CustomEvent('runai:social-mention', {}))
      return `📢 Posted to Social`

    case 'BRAIN_LEARN':
      // Open neural brain — backend already saved to wiki
      window.dispatchEvent(new CustomEvent('runai:brain-update', {}))
      return `📚 Learned: "${p1}"`

    case 'CALENDAR_ADD':
      // Open calendar and inject event
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'calendar' }))
      window.dispatchEvent(new CustomEvent('runai:calendar-add', { detail: { title: p1, date: p2 } }))
      return `📅 Calendar: "${p1}"`

    case 'CREATE_NOTE':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'runai-pad' }))
      window.dispatchEvent(new CustomEvent('runai:pad-inject', { detail: { title: p1, content: p2 } }))
      return `📝 Note: "${p1}"`

    case 'RESEARCH':
      // Open research window — backend already created project
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'research-window' }))
      window.dispatchEvent(new CustomEvent('runai:research-finding', {}))
      return `🔬 Research: "${p1}"`

    // ── Canvas Actions (RunCanvas) ─────────────────────────────────────────
    case 'CANVAS_NEW':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-canvas' }))
      window.dispatchEvent(new CustomEvent('runai:canvas-new', { detail: { type: p1 || 'design' } }))
      return `🎨 Canvas: new ${p1 || 'design'}`

    case 'CANVAS_ADD_TEXT':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-canvas' }))
      window.dispatchEvent(new CustomEvent('runai:canvas-add-text', { detail: { text: p1 || '' } }))
      return `✏️ Canvas: added text`

    case 'CANVAS_ADD_IMAGE':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-canvas' }))
      window.dispatchEvent(new CustomEvent('runai:canvas-add-image', { detail: { url: p1 || '' } }))
      return `🖼 Canvas: added image`

    case 'CANVAS_EXPORT_PDF':
      window.dispatchEvent(new CustomEvent('runai:canvas-export', { detail: { format: 'pdf' } }))
      return `📄 Canvas: exporting PDF`

    case 'CANVAS_EXPORT_PPTX':
      window.dispatchEvent(new CustomEvent('runai:canvas-export', { detail: { format: 'pptx' } }))
      return `📊 Canvas: exporting PPTX`

    // ── Office Actions (RunOffice) ─────────────────────────────────────────
    case 'OFFICE_NEW':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-office' }))
      window.dispatchEvent(new CustomEvent('runai:office-new', { detail: { type: p1 || 'doc' } }))
      return `📄 Office: new ${p1 || 'document'}`

    case 'OFFICE_INSERT_TEXT':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-office' }))
      window.dispatchEvent(new CustomEvent('runai:office-insert-text', { detail: { text: p1 || '' } }))
      return `✏️ Office: inserted text`

    // ── Music Actions (RunMusic) ───────────────────────────────────────────
    case 'PLAY_MUSIC':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-music' }))
      if (p1) window.dispatchEvent(new CustomEvent('runai:music-play', { detail: { query: p1 } }))
      return `🎵 Music: ${p1 || 'opened'}`

    // ── Workspace Actions ─────────────────────────────────────────────────────
    case 'SWITCH_WORKSPACE':
      if (p1) {
        window.dispatchEvent(new CustomEvent('runai:switch-workspace', { detail: { name: p1 } }))
        return `🗂 Workspace: ${p1}`
      }
      return null

    // ── Routine Actions ───────────────────────────────────────────────────────
    case 'RUN_ROUTINE':
      if (p1) {
        window.dispatchEvent(new CustomEvent('runai:run-routine', { detail: { name: p1 } }))
        // Store pending routine for any listener
        try { localStorage.setItem('runai_pending_routine', p1) } catch { /* */ }
        return `⚡ Running routine: ${p1}`
      }
      return null

    // ── Browser Actions (RunWeb) ───────────────────────────────────────────
    case 'BROWSE_URL':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-browser' }))
      window.dispatchEvent(new CustomEvent('runai:browser-navigate', { detail: { url: p1 || '' } }))
      return `🌐 Opening: ${p1 || ''}`

    case 'WEB_SUMMARIZE':
      window.dispatchEvent(new CustomEvent('runai:browser-summarize', {}))
      return `📄 Summarizing web page...`

    case 'ADD_BOOKMARK':
      window.dispatchEvent(new CustomEvent('runai:browser-bookmark', { detail: { url: p1 || '', title: p2 || p1 || '' } }))
      return `⭐ Bookmarked: ${p2 || p1 || ''}`

    // ── File Actions ───────────────────────────────────────────────────────
    case 'SAVE_FILE':
      // Open RunCode first, then inject after a delay to allow lazy-load mount
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-code' }))
      // Store pending inject in localStorage — RunCode picks it up on mount or after delay
      try {
        localStorage.setItem('runai_pending_inject', JSON.stringify({ filename: p1, content: p2, ts: Date.now() }))
      } catch { /* non-critical */ }
      // Also dispatch immediately (works if window already open)
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('runai:code-inject', { detail: { filename: p1, content: p2 } }))
      }, 600)
      return `💾 File: ${p1 || 'untitled'}`

    case 'READ_FILE':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-code' }))
      return `📄 Reading: ${p1 || ''}`

    case 'DELETE_FILE':
      window.dispatchEvent(new CustomEvent('runai:files-refresh', {}))
      return `🗑 Deleted: ${p1 || ''}`

    case 'LIST_FILES':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-files' }))
      window.dispatchEvent(new CustomEvent('runai:files-open-folder', { detail: { folder: p1 || 'documents' } }))
      return `📁 Files: ${p1 || 'documents'}`

    case 'CHECK_BALANCE':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'wallet-mcp' }))
      return `💰 Wallet opened`

    case 'LIST_AGENTS':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'agents-window' }))
      return `🤖 Agents list opened`

    case 'INSTALL_SKILL':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'store' }))
      // Store the skill name for StoreWindow to pick up
      try { localStorage.setItem('runai_install_skill', p1 || '') } catch { /* */ }
      return `⚡ Installing skill: ${p1}`

    case 'SET_AGENT_MODE':
      // Backend handles DB update — also dispatch event for UI feedback
      window.dispatchEvent(new CustomEvent('runai:agent-mode-change', { detail: { service: p1, enabled: p2 !== 'off' } }))
      return `🔄 Agent ${p1} mode: ${p2 || 'on'}`

    // ── Note / Pad Actions ─────────────────────────────────────────────────
    case 'WRITE_NOTE':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'runai-pad' }))
      window.dispatchEvent(new CustomEvent('runai:pad-inject', { detail: { title: p1, content: p2 } }))
      return `📝 Note: "${p1}"`

    // ── FlzHub Actions ─────────────────────────────────────────────────────
    case 'FLZHUB_POST':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'flzhub' }))
      window.dispatchEvent(new CustomEvent('runai:flzhub-message', { detail: { message: p1 } }))
      return `💬 FlzHub: "${p1 ? p1.slice(0, 40) : ''}..."`

    // ── Twin Actions ───────────────────────────────────────────────────────
    case 'TWIN_LEARN':
      // Backend handles DB write — frontend opens twin window
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'twin' }))
      window.dispatchEvent(new CustomEvent('runai:twin-update', { detail: { content: p1, type: p2 || 'learned_fact' } }))
      return `🧬 Twin learned: "${p1 ? p1.slice(0, 50) : ''}"`

    // ── Admin Actions ──────────────────────────────────────────────────────
    case 'ADMIN_ACTION':
      // Open admin panel and dispatch action
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'admin-panel' }))
      window.dispatchEvent(new CustomEvent('runai:admin-action', { detail: { action: p1, target: p2 } }))
      return `🛡 Admin: ${p1} ${p2 ? `→ ${p2}` : ''}`

    // ── Training Actions ───────────────────────────────────────────────────
    case 'START_TRAINING':
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'training' }))
      return `🧠 Training: ${p1 || 'started'}`

    // ── RunCode Actions ────────────────────────────────────────────────────
    case 'CODE_RUN':
      // Backend handles execution — frontend opens RunCode to show output
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-code' }))
      return `▶ Running code: project ${p1 || '?'}, command: ${p2 || 'default'}`

    case 'CODE_WRITE':
      // Open RunCode and inject code (backend already broadcast via WS)
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-code' }))
      try {
        localStorage.setItem('runai_pending_inject', JSON.stringify({ filename: p1, content: p2, ts: Date.now() }))
      } catch { /* non-critical */ }
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('runai:code-inject', { detail: { filename: p1, content: p2 } }))
      }, 600)
      return `💾 Code written: ${p1 || 'untitled'}`

    case 'BUILDER_OPEN':
      // Open RunCode in builder mode
      window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'run-code' }))
      window.dispatchEvent(new CustomEvent('runai:builder-open', { detail: { templateId: p1 } }))
      return `🏗 Builder opened: ${p1 || 'default template'}`

    default:
      console.warn('[AgentActions] Unknown action:', action.type, action.params)
      return null
  }
}

// ── Legacy compatibility ──────────────────────────────────────────────────────

/** @deprecated Use parseAgentActions + dispatchAgentActions */
export function parseActionTags(text: string) {
  return parseAgentActions(text).map(a => ({
    type: a.type.toLowerCase() as any,
    payload: Object.fromEntries(a.params.map((p, i) => [`param${i}`, p])),
    raw: a.raw,
  }))
}

/** @deprecated Use dispatchAgentActions */
export function dispatchActions(_actions: any[]) {
  // no-op for legacy callers
}

/** @deprecated Use stripActionTags */
export function handleBackendActions(actions: string[] | undefined) {
  if (!actions) return
  for (const action of actions) {
    if (action.includes('🪟')) {
      const windowId = action.split(':').pop()?.trim()
      if (windowId) window.dispatchEvent(new CustomEvent('runai:open-window', { detail: windowId }))
    }
    if (action.includes('✅')) {
      window.dispatchEvent(new CustomEvent('runai:task-alert', { detail: { type: 'created' } }))
    }
  }
}
